import { z } from "zod";

// =========================================================================
// CONSTANTS
// =========================================================================

/**
 * Supported granularities for the timeseries endpoint.
 * The backend picks the granularity automatically based on date range
 * (see GRANULARITY_THRESHOLDS), but the caller can override it.
 */
export const ANALYTICS_GRANULARITIES = ["day", "week", "month"] as const;
export type AnalyticsGranularity = (typeof ANALYTICS_GRANULARITIES)[number];

export const GRANULARITY_THRESHOLDS = { week: 31, month: 90 } as const;

export const ANALYTICS_BATCH_MAX_EVENTS = 20 as const;

export const FLUSH_THRESHOLD = 50 as const;

// =========================================================================
// SHARED PRIMITIVES
// =========================================================================

const IsoDateString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, {
  message: "Expected ISO date (YYYY-MM-DD)",
});

const DateRangeSchema = z.object({
  date_from: IsoDateString,
  date_to: IsoDateString,
});

// =========================================================================
// WIDGET → BACKEND
// =========================================================================

// ── POST /analytics/events ────────────────────────────────────────────────
//
// Single batch endpoint for all widget-side tracking events.
// Called via navigator.sendBeacon(), fire-and-forget, does not block the
// buyer's experience. Losing ~1-2% of events on network failure is
// acceptable for impression counting (not financial data).
//
// Backend processes all events in a single Redis pipeline (one connection,
// N INCRs), then checks each key against FLUSH_THRESHOLD.

const ImpressionEventSchema = z.object({
  type: z.literal("impression"),
  form_id: z.cuid(),
});

const OfferShownEventSchema = z.object({
  type: z.literal("offer_shown"),
  offer_id: z.cuid(),
});

const ProductPageViewEventSchema = z.object({
  type: z.literal("product_page_view"),
  form_id: z.cuid(),
});

const AnalyticsEventSchema = z.discriminatedUnion("type", [
  ImpressionEventSchema,
  OfferShownEventSchema,
  ProductPageViewEventSchema,
]);

export const TrackEventsRequestSchema = z.object({
  shop: z.string().min(1),
  events: z.array(AnalyticsEventSchema).min(1).max(ANALYTICS_BATCH_MAX_EVENTS),
});

export const TrackEventsResponseSchema = z.object({
  ok: z.literal(true),
});

// =========================================================================
// INTERNAL  (called by scheduler, not exposed publicly)
// =========================================================================

// ── POST /internal/analytics/flush ────────────────────────────────────────
//
// Reads all pending redis counters (impression and offer_shown keys) and
// upserts them into form_analytics_daily / offer_metrics via a single
// Prisma transaction per shop-date pair.
//
// Redis key conventions:
//   analytics:imp:{shop_id}:{form_id}:{YYYY-MM-DD}    → impression counter
//   analytics:pdp:{shop_id}:{form_id}:{YYYY-MM-DD}      → product_page_view counter
//   analytics:off:{shop_id}:{offer_id}:{YYYY-MM-DD}   → offer_shown counter

export const FlushAnalyticsRequestSchema = z.object({
  shop_id: z.cuid().optional(),
});

export const FlushAnalyticsResponseSchema = z.object({
  flushed_impression_keys: z.number().int().nonnegative(),
  flushed_product_page_view_keys: z.number().int().nonnegative().optional(),
  flushed_offer_keys: z.number().int().nonnegative(),
  duration_ms: z.number().int().nonnegative(),
});

// =========================================================================
// ADMIN  (merchant-facing dashboard)
// =========================================================================

// ── Shared query params ───────────────────────────────────────────────────

/**
 * Base query params shared by all admin analytics endpoints.
 * All endpoints are scoped to the authenticated shop.
 * Adding a new filter here propagates it to all three endpoints automatically.
 */
const AdminAnalyticsBaseQuerySchema = DateRangeSchema.extend({
  /** Filter by a specific form. Preparation for future n forms per shop. */
  form_id: z.cuid().optional(),
  country_id: z.cuid().optional(),
});

// ── GET /admin/analytics/summary ─────────────────────────────────────────
//
// KPI cards for the dashboard header.
// The equivalent prior period (same number of days, immediately before
// date_from) is always computed server-side so the frontend only renders
// values and deltas without any date arithmetic.

export const AdminSummaryQuerySchema = AdminAnalyticsBaseQuerySchema;

const KpiMetricSchema = z.object({
  value: z.number(),
  previous_value: z.number(),
  /**
   * Percentage change vs previous period.
   * Null when previous_value is 0 to avoid division by zero on the wire.
   */
  change_pct: z.number().nullable(),
  is_positive: z.boolean(),
});

export const AdminSummaryResponseSchema = z.object({
  period: DateRangeSchema,
  previous_period: DateRangeSchema,

  total_orders: KpiMetricSchema,
  completed_orders: KpiMetricSchema,
  /** Gross revenue from completed orders (subtotal, no shipping). */
  total_revenue: KpiMetricSchema,
  upsell_revenue: KpiMetricSchema,
  /** completed_orders / impressions × 100. */
  conversion_rate: KpiMetricSchema,
  /**
   * submissions / product_page_views × 100.
   * The backend may compute values above 100 during rollout when historical
   * submissions outpace new product_page_views counters; the admin UI shows —.
   */
  product_page_conversion_rate: KpiMetricSchema,
  /** total_revenue / completed_orders. */
  avg_order_value: KpiMetricSchema,
  impressions: KpiMetricSchema,
  /** Product detail page visits where the Codway widget is shown. */
  product_page_views: KpiMetricSchema,
  fraud_blocked: KpiMetricSchema,
});

// ── GET /admin/analytics/timeseries ──────────────────────────────────────
//
// Time-bucketed series for charting. All metrics are returned in every
// point so the frontend can render any combination without a new endpoint.
// The optional `metrics` filter lets the caller reduce payload size for
// simple single-metric charts — the backend does a narrower SELECT.

export const AdminTimeseriesQuerySchema = AdminAnalyticsBaseQuerySchema.extend({
  /**
   * Bucket size. Auto-selected when omitted:
   *   ≤ 31 days → day | ≤ 90 days → week | > 90 days → month
   */
  granularity: z.enum(ANALYTICS_GRANULARITIES).optional(),
  metrics: z
    .array(
      z.enum([
        "impressions",
        "product_page_views",
        "submissions",
        "completed_orders",
        "failed_orders",
        "fraud_blocked",
        "total_revenue",
        "upsell_revenue",
        "avg_order_value",
        "conversion_rate",
        "product_page_conversion_rate",
      ])
    )
    .optional(),
});

const TimeseriesPointSchema = z.object({
  date: IsoDateString,
  impressions: z.number().int().nonnegative(),
  product_page_views: z.number().int().nonnegative(),
  submissions: z.number().int().nonnegative(),
  completed_orders: z.number().int().nonnegative(),
  failed_orders: z.number().int().nonnegative(),
  fraud_blocked: z.number().int().nonnegative(),
  total_revenue: z.number().nonnegative(),
  upsell_revenue: z.number().nonnegative(),
  /** Null when completed_orders = 0 for the bucket. */
  avg_order_value: z.number().nonnegative().nullable(),
  /** Null when impressions = 0 for the bucket. */
  conversion_rate: z.number().nonnegative().nullable(),
  /**
   * Null when product_page_views = 0 for the bucket.
   * Raw values above 100 are possible during rollout; the admin UI shows —.
   */
  product_page_conversion_rate: z.number().nonnegative().nullable(),
});

export const AdminTimeseriesResponseSchema = z.object({
  period: DateRangeSchema,
  granularity: z.enum(ANALYTICS_GRANULARITIES),
  points: z.array(TimeseriesPointSchema),
});

// ── GET /admin/analytics/offers ──────────────────────────────────────────
//
// Per-offer funnel: shown → accepted → revenue.
// Includes all offers that had at least one event in the period.
// Sorted by total_revenue descending.

export const AdminOffersQuerySchema = AdminAnalyticsBaseQuerySchema;

const OfferBreakdownItemSchema = z.object({
  offer_id: z.cuid(),
  offer_name: z.string(),
  // Add offers as needed, but these are the ones we have now
  offer_display_type: z.enum(["modal", "banner", "quantity_tiers", "one_tick"]),

  times_shown: z.number().int().nonnegative(),
  times_accepted: z.number().int().nonnegative(),
  times_rejected: z.number().int().nonnegative(),

  /**
   * times_accepted / (times_accepted + times_rejected).
   * Null when no decisions recorded yet.
   */
  acceptance_rate: z.number().nonnegative().nullable(),

  total_revenue: z.number().nonnegative(),
  /** Null when times_accepted = 0. */
  avg_revenue_per_accept: z.number().nonnegative().nullable(),

  /**
   * Median ms from offer shown to accept/reject decision.
   * Null when no timing data is available.
   */
  median_decided_ms: z.number().int().nonnegative().nullable(),
});

export const AdminOffersResponseSchema = z.object({
  period: DateRangeSchema,
  offers: z.array(OfferBreakdownItemSchema),
  totals: z.object({
    times_shown: z.number().int().nonnegative(),
    times_accepted: z.number().int().nonnegative(),
    total_revenue: z.number().nonnegative(),
  }),
});

// =========================================================================
// TYPE EXPORTS
// =========================================================================

export type AnalyticsEvent = z.infer<typeof AnalyticsEventSchema>;
export type TrackEventsRequest = z.infer<typeof TrackEventsRequestSchema>;
export type TrackEventsResponse = z.infer<typeof TrackEventsResponseSchema>;
export type FlushAnalyticsRequest = z.infer<typeof FlushAnalyticsRequestSchema>;
export type FlushAnalyticsResponse = z.infer<
  typeof FlushAnalyticsResponseSchema
>;
export type AdminSummaryQuery = z.infer<typeof AdminSummaryQuerySchema>;
export type AdminSummaryResponse = z.infer<typeof AdminSummaryResponseSchema>;
export type AdminTimeseriesQuery = z.infer<typeof AdminTimeseriesQuerySchema>;
export type AdminTimeseriesResponse = z.infer<
  typeof AdminTimeseriesResponseSchema
>;
export type AdminOffersQuery = z.infer<typeof AdminOffersQuerySchema>;
export type AdminOffersResponse = z.infer<typeof AdminOffersResponseSchema>;
export type TimeseriesPoint = z.infer<typeof TimeseriesPointSchema>;
export type OfferBreakdownItem = z.infer<typeof OfferBreakdownItemSchema>;
export type KpiMetric = z.infer<typeof KpiMetricSchema>;
